/**
 * Passkey (WebAuthn) API service
 *
 * Handles all interactions with the passkey backend endpoints and
 * the WebAuthn browser API (navigator.credentials).
 *
 * Security notes:
 * - All ArrayBuffer ↔ base64url conversions are handled here
 * - navigator.credentials.create/get are only called with validated options
 * - Errors from the browser (user cancellation, etc.) propagate as thrown errors
 */
import api, { getErrorMessage } from './api'
import {
  isPasskeySupported,
  isConditionalMediationAvailable,
  prepareAuthenticationOptions,
  prepareRegistrationOptions,
  serializeAuthenticationCredential,
  serializeRegistrationCredential,
} from './webauthn'
import type {
  Passkey,
  PasskeyRegisterBeginRequest,
  PasskeyRegisterBeginResponse,
  PasskeyRegisterCompleteRequest,
  PasskeyAuthBeginRequest,
  PasskeyAuthBeginResponse,
  PasskeyAuthCompleteRequest,
  TokenResponse,
} from '../types'

// Re-export so existing importers (LoginPage, ProfilePage, UsersPage) that pull
// isPasskeySupported from this module keep working after the helper extraction.
export { isPasskeySupported, isConditionalMediationAvailable }

// ---------------------------------------------------------------------------
// Self-service passkey API (current user)
// ---------------------------------------------------------------------------

export async function listPasskeys(): Promise<Passkey[]> {
  try {
    const response = await api.get<Passkey[]>('/v1/auth/passkeys')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Full registration flow: begin → browser prompt → complete.
 * Returns the newly created Passkey on success.
 */
export async function enrollPasskey(
  label: string,
  currentPassword: string,
  mfaCode?: string,
  reauthToken?: string,
): Promise<Passkey> {
  // Step 1: get options from server (requires re-auth). The second factor is
  // either an authenticator/backup code (mfaCode) or a fresh passkey assertion
  // exchanged for a reauth_token — a passkey-only user has no code to type.
  const beginReq: PasskeyRegisterBeginRequest = {
    passkey_label: label,
    current_password: currentPassword,
    mfa_code: mfaCode || undefined,
    reauth_token: reauthToken || undefined,
  }
  let beginResp: PasskeyRegisterBeginResponse
  try {
    const resp = await api.post<PasskeyRegisterBeginResponse>(
      '/v1/auth/passkey/register/begin',
      beginReq,
    )
    beginResp = resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }

  // Step 2: invoke browser WebAuthn prompt
  const publicKeyOptions = prepareRegistrationOptions(beginResp.options)
  let credential: PublicKeyCredential
  try {
    credential = (await navigator.credentials.create({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    // Rethrow with a friendlier message for cancellation
    if (msg.includes('cancel') || msg.includes('abort') || msg.includes('NotAllowed')) {
      throw new Error('Passkey registration was cancelled.')
    }
    throw new Error(`Browser error: ${msg}`)
  }

  if (!credential) {
    throw new Error('No credential returned by the browser.')
  }

  // Step 3: send credential to server to verify and store
  const completeReq: PasskeyRegisterCompleteRequest = {
    passkey_label: label,
    challenge_token: beginResp.challenge_token,
    credential: serializeRegistrationCredential(credential),
  }
  try {
    const resp = await api.post<Passkey>('/v1/auth/passkey/register/complete', completeReq)
    return resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deletePasskey(
  passkeyId: number,
  currentPassword: string,
  mfaCode?: string,
  reauthToken?: string,
): Promise<void> {
  try {
    await api.delete(`/v1/auth/passkeys/${passkeyId}`, {
      data: {
        current_password: currentPassword,
        mfa_code: mfaCode || undefined,
        reauth_token: reauthToken || undefined,
      },
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Passkey login flow (public — no existing session required)
// ---------------------------------------------------------------------------

/**
 * Full authentication flow: begin → browser prompt → complete.
 * Returns TokenResponse on success (caller stores token + navigates).
 */
export async function authenticateWithPasskey(email?: string): Promise<TokenResponse> {
  // Step 1: get assertion options from server
  const beginReq: PasskeyAuthBeginRequest = email ? { email } : {}
  let beginResp: PasskeyAuthBeginResponse
  try {
    const resp = await api.post<PasskeyAuthBeginResponse>(
      '/v1/auth/passkey/authenticate/begin',
      beginReq,
    )
    beginResp = resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }

  // Step 2: browser WebAuthn assertion prompt
  const publicKeyOptions = prepareAuthenticationOptions(beginResp.options)
  let credential: PublicKeyCredential
  try {
    credential = (await navigator.credentials.get({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('cancel') || msg.includes('abort') || msg.includes('NotAllowed')) {
      throw new Error('Passkey sign-in was cancelled.')
    }
    throw new Error(`Browser error: ${msg}`)
  }

  if (!credential) {
    throw new Error('No credential returned by the browser.')
  }

  // Step 3: verify assertion on server → returns full session token
  const completeReq: PasskeyAuthCompleteRequest = {
    challenge_token: beginResp.challenge_token,
    credential: serializeAuthenticationCredential(credential),
  }
  try {
    const resp = await api.post<TokenResponse>(
      '/v1/auth/passkey/authenticate/complete',
      completeReq,
    )
    return resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Conditional-UI ("passkey autofill") authentication.
 *
 * Starts a discoverable-credential assertion with `mediation: 'conditional'` so
 * the browser offers any saved passkey for this site in the username field's
 * autofill dropdown — the ceremony only fires if the user picks one. This is
 * purely additive to the explicit "Sign in with a passkey" button.
 *
 * The passed AbortSignal lets the caller cancel the pending request (e.g. on
 * unmount or when the user submits the password form instead).
 *
 * Returns a TokenResponse on success, or `null` if conditional UI is
 * unavailable / the request was aborted (the caller should treat null as "no
 * passkey was used" and fall back to the normal login form).
 */
export async function authenticateWithPasskeyConditional(
  signal: AbortSignal,
): Promise<TokenResponse | null> {
  if (!(await isConditionalMediationAvailable())) {
    return null
  }

  // Begin with no email → discoverable-credential options (empty allow-list).
  let beginResp: PasskeyAuthBeginResponse
  try {
    const resp = await api.post<PasskeyAuthBeginResponse>(
      '/v1/auth/passkey/authenticate/begin',
      {},
    )
    beginResp = resp.data
  } catch {
    return null
  }

  const publicKeyOptions = prepareAuthenticationOptions(beginResp.options)
  let credential: PublicKeyCredential | null
  try {
    credential = (await navigator.credentials.get({
      publicKey: publicKeyOptions,
      mediation: 'conditional',
      signal,
    } as CredentialRequestOptions)) as PublicKeyCredential | null
  } catch {
    // Aborted, cancelled, or unsupported — fall back to the form silently.
    return null
  }
  if (!credential) {
    return null
  }

  const completeReq: PasskeyAuthCompleteRequest = {
    challenge_token: beginResp.challenge_token,
    credential: serializeAuthenticationCredential(credential),
  }
  const resp = await api.post<TokenResponse>(
    '/v1/auth/passkey/authenticate/complete',
    completeReq,
  )
  return resp.data
}

export interface ReauthResult {
  token: string
  expires_in_seconds: number
}

/**
 * Re-authenticate the current admin with a passkey to mint a short-lived
 * admin re-auth token (same token password+MFA re-auth returns), for
 * authorizing sensitive admin actions. begin → browser prompt → complete.
 */
export async function reauthWithPasskey(): Promise<ReauthResult> {
  let beginResp: PasskeyAuthBeginResponse
  try {
    const resp = await api.post<PasskeyAuthBeginResponse>('/v1/auth/reauth/passkey/begin', {})
    beginResp = resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }

  const publicKeyOptions = prepareAuthenticationOptions(beginResp.options)
  let credential: PublicKeyCredential
  try {
    credential = (await navigator.credentials.get({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('cancel') || msg.includes('abort') || msg.includes('NotAllowed')) {
      throw new Error('Passkey verification was cancelled.')
    }
    throw new Error(`Browser error: ${msg}`)
  }
  if (!credential) {
    throw new Error('No credential returned by the browser.')
  }

  const completeReq: PasskeyAuthCompleteRequest = {
    challenge_token: beginResp.challenge_token,
    credential: serializeAuthenticationCredential(credential),
  }
  try {
    const resp = await api.post<ReauthResult>('/v1/auth/reauth/passkey/complete', completeReq)
    return resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Admin passkey management (requires admin session)
// ---------------------------------------------------------------------------

export async function adminListPasskeys(userId: number): Promise<Passkey[]> {
  try {
    const response = await api.get<Passkey[]>(`/v1/users/${userId}/passkeys`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminDeletePasskey(
  userId: number,
  passkeyId: number,
  reauthToken?: string | null,
): Promise<void> {
  try {
    await api.delete(`/v1/users/${userId}/passkeys/${passkeyId}`, {
      headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined,
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function renamePasskey(passkeyId: number, newLabel: string): Promise<void> {
  try {
    await api.patch(`/v1/auth/passkeys/${passkeyId}`, { label: newLabel })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Full admin passkey enrollment flow for a target user:
 * begin → browser WebAuthn prompt → complete.
 * The WebAuthn ceremony runs in the admin's browser; the passkey is saved
 * under the target user's account (not the admin's).
 */
export async function adminEnrollPasskey(
  userId: number,
  label: string,
  reauthToken?: string | null,
): Promise<Passkey> {
  // Step 1: get registration options (bound to target user's identity)
  const beginResp = await adminEnrollPasskeyBegin(userId, label, reauthToken)

  // Step 2: browser WebAuthn prompt
  const publicKeyOptions = prepareRegistrationOptions(beginResp.options)
  let credential: PublicKeyCredential
  try {
    credential = (await navigator.credentials.create({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('cancel') || msg.includes('abort') || msg.includes('NotAllowed')) {
      throw new Error('Passkey registration was cancelled.')
    }
    throw new Error(`Browser error: ${msg}`)
  }

  if (!credential) {
    throw new Error('No credential returned by the browser.')
  }

  // Step 3: verify and save under target user
  return adminEnrollPasskeyComplete(
    userId,
    label,
    beginResp.challenge_token,
    serializeRegistrationCredential(credential),
    reauthToken,
  )
}

export async function adminEnrollPasskeyBegin(
  userId: number,
  label: string,
  reauthToken?: string | null,
): Promise<PasskeyRegisterBeginResponse> {
  try {
    const response = await api.post<PasskeyRegisterBeginResponse>(
      `/v1/users/${userId}/passkeys/register/begin`,
      { passkey_label: label },
      { headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminEnrollPasskeyComplete(
  userId: number,
  label: string,
  challengeToken: string,
  credential: Record<string, unknown>,
  reauthToken?: string | null,
): Promise<Passkey> {
  try {
    const response = await api.post<Passkey>(
      `/v1/users/${userId}/passkeys/register/complete`,
      {
        passkey_label: label,
        challenge_token: challengeToken,
        credential,
      },
      { headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
